import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../../services/api';
import { useHelpContent } from '../../utils/HelpUtils';

interface SubcaseGroupsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the document shell can refresh group chips elsewhere. */
  onSaved?: () => void;
  documentId: string;
  documentSubcases: string[];
}

interface SubcaseGroup {
  name: string;
  subcases: string[];
}

export const SubcaseGroupsDialog: React.FC<SubcaseGroupsDialogProps> = ({
  open,
  onClose,
  onSaved,
  documentId,
  documentSubcases,
}) => {
  const [groups, setGroups] = useState<SubcaseGroup[]>([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedSubcases, setSelectedSubcases] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalGroups, setOriginalGroups] = useState<SubcaseGroup[]>([]);
  const { getTooltipTextForState } = useHelpContent();

  useEffect(() => {
    if (open && documentId) {
      loadGroups();
    }
  }, [open, documentId]);

  const loadGroups = async () => {
    try {
      const response = await api.get('/documents/subcase-groups' ,{ params: { documentId} });
      const groupsMap = response.data;
      const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
        name,
        subcases: subcases as string[],
      }));
      setGroups(groupsArray);
      setOriginalGroups(groupsArray);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load subcase groups:', error);
    }
  };

  const handleAddGroup = () => {
    if (!newGroupName.trim()) return;

    const newGroup: SubcaseGroup = {
      name: newGroupName,
      subcases: [],
    };

    setGroups([...groups, newGroup]);
    setNewGroupName('');
    setSelectedGroup(newGroup.name);
    setHasChanges(true);
  };

  const handleDeleteGroup = (groupName: string) => {
    setGroups(groups.filter(g => g.name !== groupName));
    if (selectedGroup === groupName) {
      setSelectedGroup(null);
    }
    setHasChanges(true);
  };

  const handleAddSubcaseToGroup = (groupName: string, subcases: string[]) => {
    setGroups(groups.map(group => {
      if (group.name === groupName) {
        return {
          ...group,
          subcases: [...group.subcases, ...subcases],
        };
      }
      return group;
    }));
    setSelectedSubcases([]);
    setHasChanges(true);
  };

  const handleRemoveSubcaseFromGroup = (groupName: string, subcase: string) => {
    setGroups(groups.map(group => {
      if (group.name === groupName) {
        return {
          ...group,
          subcases: group.subcases.filter(s => s !== subcase),
        };
      }
      return group;
    }));
    setHasChanges(true);
  };

  const handleCancel = () => {
    setGroups(originalGroups);
    setHasChanges(false);
    onClose();
  };

  const persistGroups = async (): Promise<boolean> => {
    try {
      const groupsMap = groups.reduce((acc, group) => {
        acc[group.name] = group.subcases;
        return acc;
      }, {} as Record<string, string[]>);
      await api.post('/documents/subcase-groups', groupsMap, { params: { documentId } });
      await loadGroups();
      onSaved?.();
      return true;
    } catch (error) {
      console.error('Failed to save subcase groups:', error);
      return false;
    }
  };

  const handleSave = async () => {
    await persistGroups();
  };

  /** Save pending edits if any, then close (without reverting like Cancel). */
  const handleDone = async () => {
    if (hasChanges) {
      const ok = await persistGroups();
      if (!ok) return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Manage Subcase Groups</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Add New Group
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={getTooltipTextForState('add-category', true)} placement="top">
              <TextField
                label="Group Name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                fullWidth
              />
            </Tooltip>
            <Tooltip title={getTooltipTextForState('add-category', true)} placement="top">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddGroup}
                disabled={!newGroupName.trim()}
              >
                Add Group
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Groups
            </Typography>
            <Tooltip title={getTooltipTextForState('manage-subcase-groups', true)} placement="top">
              <List>
                {groups.map((group) => (
                  <ListItem
                    key={group.name}
                    selected={selectedGroup === group.name}
                    onClick={() => setSelectedGroup(group.name)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ListItemText primary={group.name} />
                    <ListItemSecondaryAction>
                      <Tooltip title={getTooltipTextForState('delete-category', true)} placement="top">
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteGroup(group.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Tooltip>
          </Box>

          {selectedGroup && (
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Group Subcases
              </Typography>
              <List>
                {documentSubcases.map((subcase) => {
                  const group = groups.find(g => g.name === selectedGroup);
                  const checked = group?.subcases.includes(subcase) || false;
                  return (
                    <ListItem key={subcase} dense button onClick={() => {
                      if (checked) {
                        handleRemoveSubcaseFromGroup(selectedGroup, subcase);
                      } else {
                        handleAddSubcaseToGroup(selectedGroup, [subcase]);
                      }
                    }}>
                      <Tooltip title={getTooltipTextForState('find-subcase-groups', true)} placement="top">
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          onChange={() => {
                            if (checked) {
                              handleRemoveSubcaseFromGroup(selectedGroup, subcase);
                            } else {
                              handleAddSubcaseToGroup(selectedGroup, [subcase]);
                            }
                          }}
                        />
                      </Tooltip>
                      <ListItemText primary={subcase} />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip title="Discard changes and close" placement="top">
          <Button onClick={handleCancel}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState('content-save', true)} placement="top">
          <Button onClick={handleSave} disabled={!hasChanges}>
            Save
          </Button>
        </Tooltip>
        <Tooltip title="Save any pending changes and close" placement="top">
          <Button onClick={handleDone} variant="contained" color="primary">
            Done
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}; 